// A tiny typed fetch wrapper shared by the Event view and (later) the admin views.
// Cookie session, JSON in and out, and one error type that carries the status
// and the API's `reason` so callers branch on `err.reason`, not on message text.

export class ApiError extends Error {
  status: number
  reason: string
  body: Record<string, unknown>
  constructor(status: number, body: Record<string, unknown> = {}) {
    const reason = typeof body.reason === 'string' ? body.reason : ''
    const detail = typeof body.detail === 'string' ? body.detail : ''
    super(detail || reason || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.reason = reason
    this.body = body
  }
}

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
export type MockHandler = (method: Method, path: string, body: unknown) => Promise<{ status: number; body: unknown }>

let mockHandler: MockHandler | null = null
export function setMockHandler(h: MockHandler | null) {
  mockHandler = h
}

const MOCK_KEY = 'ea-mock-search'

/**
 * `?mock=1` in the URL or VITE_MOCK=1 at build/dev time: answer from fixtures, no backend.
 * The query string is remembered for the tab (sessionStorage), because the router drops it
 * on the first navigation and a reload would otherwise fall out of mock mode. `?mock=0` clears it.
 */
export function mockSearch(): string | null {
  try {
    const here = window.location.search
    const p = new URLSearchParams(here)
    if (p.get('mock') === '0') {
      sessionStorage.removeItem(MOCK_KEY)
      return import.meta.env.VITE_MOCK === '1' ? '?mock=1' : null
    }
    if (p.has('mock')) {
      sessionStorage.setItem(MOCK_KEY, here)
      return here
    }
    const stored = sessionStorage.getItem(MOCK_KEY)
    if (stored) return stored
  } catch {
    /* storage blocked: the URL alone decides */
    return new URLSearchParams(window.location.search).has('mock') ? window.location.search : import.meta.env.VITE_MOCK === '1' ? '?mock=1' : null
  }
  return import.meta.env.VITE_MOCK === '1' ? '?mock=1' : null
}
export const mockRequested = () => mockSearch() !== null

export async function api<T = unknown>(method: Method, path: string, body?: unknown): Promise<T> {
  if (mockHandler) {
    const res = await mockHandler(method, path, body)
    if (res.status >= 400) throw new ApiError(res.status, (res.body ?? {}) as Record<string, unknown>)
    return res.body as T
  }
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (res.status === 204) return undefined as T
  let data: unknown = null
  const text = await res.text()
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = { detail: text }
    }
  }
  if (!res.ok) throw new ApiError(res.status, (data ?? {}) as Record<string, unknown>)
  return data as T
}

export const get = <T>(path: string) => api<T>('GET', path)
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body ?? {})
export const put = <T>(path: string, body?: unknown) => api<T>('PUT', path, body ?? {})
export const patch = <T>(path: string, body?: unknown) => api<T>('PATCH', path, body ?? {})
export const del = <T>(path: string) => api<T>('DELETE', path)
